import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  BookOpen, 
  TrendingUp, 
  Activity,
  Award,
  Clock,
  BarChart3,
  PieChart,
  Calendar
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const generateMockTrendData = () => {
  const days = 30;
  const data = [];
  const today = new Date();
  
  for (let i = days; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    data.push({
      date: date.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }),
      users: Math.floor(50 + Math.random() * 30 + (days - i) * 2),
      questions: Math.floor(100 + Math.random() * 50 + (days - i) * 3),
      activeStudents: Math.floor(20 + Math.random() * 20 + (days - i))
    });
  }
  
  return data;
};

const PlatformAnalyticsPage = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7days'); // 7days, 30days, all

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch multiple analytics endpoints
      const [usersRes, questionsRes, plansRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/questions/bank`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/subscriptions/plans`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const users = usersRes.ok ? await usersRes.json() : { users: [] };
      const questions = questionsRes.ok ? await questionsRes.json() : { questions: [] };
      const plans = plansRes.ok ? await plansRes.json() : { plans: [] };

      // Calculate analytics
      const totalUsers = users.users?.length || 0;
      const studentCount = users.users?.filter(u => u.role === 'student').length || 0;
      const teacherCount = users.users?.filter(u => u.role === 'teacher').length || 0;
      const adminCount = users.users?.filter(u => u.role === 'admin' || u.role === 'super_admin').length || 0;

      const totalQuestions = questions.questions?.length || 0;
      const tytQuestions = questions.questions?.filter(q => q.exam_context?.includes('TYT')).length || 0;
      const apQuestions = questions.questions?.filter(q => q.exam_context?.includes('AP')).length || 0;

      // Subject distribution
      const subjectCount = {};
      questions.questions?.forEach(q => {
        const subject = q.subject || 'Unknown';
        subjectCount[subject] = (subjectCount[subject] || 0) + 1;
      });

      // Difficulty distribution
      const difficultyCount = {
        easy: questions.questions?.filter(q => q.difficulty === 'easy').length || 0,
        medium: questions.questions?.filter(q => q.difficulty === 'medium').length || 0,
        hard: questions.questions?.filter(q => q.difficulty === 'hard').length || 0
      };

      // Generate mock trend data (in production, this should come from backend)
      const trendData = generateMockTrendData();
      
      setAnalytics({
        users: {
          total: totalUsers,
          students: studentCount,
          teachers: teacherCount,
          admins: adminCount
        },
        questions: {
          total: totalQuestions,
          tyt: tytQuestions,
          ap: apQuestions,
          bySubject: subjectCount,
          byDifficulty: difficultyCount
        },
        plans: plans.plans || [],
        trends: trendData
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center space-x-3">
                <BarChart3 className="h-8 w-8 text-indigo-600" />
                <span>Platform Analitikleri</span>
              </h1>
              <p className="text-gray-600">Genel platform istatistikleri ve metrikler</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              ← Geri Dön
            </button>
          </div>
        </div>

        {/* Time Range Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Zaman Aralığı:</span>
            <div className="flex space-x-2">
              {[
                { value: '7days', label: 'Son 7 Gün' },
                { value: '30days', label: 'Son 30 Gün' },
                { value: 'all', label: 'Tüm Zamanlar' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setTimeRange(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    timeRange === option.value
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* User Statistics */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Users className="h-6 w-6 text-indigo-600" />
            <span>Kullanıcı İstatistikleri</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">Toplam Kullanıcı</span>
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{analytics?.users.total || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">Öğrenciler</span>
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-green-700">{analytics?.users.students || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">Öğretmenler</span>
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-purple-700">{analytics?.users.teachers || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">Adminler</span>
                <Users className="h-5 w-5 text-red-600" />
              </div>
              <p className="text-3xl font-bold text-red-700">{analytics?.users.admins || 0}</p>
            </div>
          </div>
        </div>

        {/* Question Statistics */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <BookOpen className="h-6 w-6 text-indigo-600" />
            <span>Soru Bankası İstatistikleri</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">Toplam Soru</span>
                <BookOpen className="h-5 w-5 text-indigo-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{analytics?.questions.total || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">TYT Soruları</span>
                <BookOpen className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-blue-700">{analytics?.questions.tyt || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 text-sm">AP Soruları</span>
                <BookOpen className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-green-700">{analytics?.questions.ap || 0}</p>
            </div>
          </div>

          {/* Subject Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Ders Dağılımı</h3>
            <div className="space-y-3">
              {Object.entries(analytics?.questions.bySubject || {}).map(([subject, count]) => (
                <div key={subject} className="flex items-center">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{subject}</span>
                      <span className="text-sm text-gray-600">{count} soru</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${(count / (analytics?.questions.total || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Difficulty Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Zorluk Dağılımı</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="bg-green-100 rounded-lg p-4 mb-2">
                  <p className="text-2xl font-bold text-green-700">{analytics?.questions.byDifficulty.easy || 0}</p>
                </div>
                <p className="text-sm text-gray-600">Kolay</p>
              </div>
              <div className="text-center">
                <div className="bg-yellow-100 rounded-lg p-4 mb-2">
                  <p className="text-2xl font-bold text-yellow-700">{analytics?.questions.byDifficulty.medium || 0}</p>
                </div>
                <p className="text-sm text-gray-600">Orta</p>
              </div>
              <div className="text-center">
                <div className="bg-red-100 rounded-lg p-4 mb-2">
                  <p className="text-2xl font-bold text-red-700">{analytics?.questions.byDifficulty.hard || 0}</p>
                </div>
                <p className="text-sm text-gray-600">Zor</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trend Charts */}
        {analytics?.trends && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
              <TrendingUp className="h-6 w-6 text-indigo-600" />
              <span>Kullanım Trendleri (Son 30 Gün)</span>
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* User Growth Chart */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Kullanıcı Büyümesi</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                    <YAxis style={{ fontSize: '12px' }} />
                    <Tooltip />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="users" 
                      name="Toplam Kullanıcı"
                      stroke="#6366f1" 
                      fill="#6366f1" 
                      fillOpacity={0.6}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="activeStudents" 
                      name="Aktif Öğrenciler"
                      stroke="#10b981" 
                      fill="#10b981" 
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Question Activity Chart */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Soru Bankası Aktivitesi</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                    <YAxis style={{ fontSize: '12px' }} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="questions" 
                      name="Eklenen Sorular"
                      stroke="#8b5cf6" 
                      strokeWidth={3}
                      dot={{ fill: '#8b5cf6', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Subject Distribution Pie Chart */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Ders Dağılımı (Pasta Grafiği)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={Object.entries(analytics?.questions.bySubject || {}).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.keys(analytics?.questions.bySubject || {}).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Subscription Plans */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Award className="h-6 w-6 text-indigo-600" />
            <span>Abonelik Planları</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {analytics?.plans.map((plan) => (
              <div key={plan.id} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{plan.display_name_tr}</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-600">
                    <span className="font-medium">Aylık:</span> ₺{plan.price_monthly}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Yıllık:</span> ₺{plan.price_yearly}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">AI Mesaj Limiti:</span> {plan.limits?.chat_messages || 0}/gün
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Soru Üretimi:</span> {plan.limits?.ai_generations || 0}/gün
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAnalyticsPage;
